/**
 * router/index.ts
 *
 * Purpose:
 *   Collects all of the (top-level) pages listed in the /routes/index.ts file
 *   and builds them into routes to be served by the application.
 *   Any top-level page not registered in the /pages/indexjs file
 *   may not show up when navigated to.
 *   We use some fancy react features to lazy load in new pages
 *   with react's Suspense system.
 *
 * Things to Change:
 *   If you have very specific views about how pages should be routed using react,
 *   then change them around here.
 *   Also if you want to use something like helmet
 *   to give each page a specific title you can add that in here as well.
 *   Basically anything having to do with switching between pages
 *   can be changed around in this file.
 */

import React, { lazy, Suspense } from "react";
import { Route, Routes } from "react-router";
import PageLoading from "../components/page-loading";
import { AnyType } from "../interfaces";
import AuthService from "../services/auth-service";
import pageComponents from "../pages";
import NotFound from "../pages/NotFound";
import appRoutes from "../routes";
import ProtectedRoute from "./protected-route";

interface RouteConfig {
  path: string;
  component?: string;
  authRequired?: boolean;
}

function AppRoutes (): React.JSX.Element {
  const routes = appRoutes.map(currRoute => {
    const route: RouteConfig = { ...currRoute };
    const { component } = route;

    delete route.component;

    const Component = (props: Record<string, AnyType>) => {
      if(!component) { 
        throw new Error("Component name is required"); 
      }

      const InternalComponent = lazy(() => pageComponents[component]);

      return (
        <Suspense fallback={ <PageLoading /> }>
          <InternalComponent {...props} />
        </Suspense>
      );
    };

    return { ...route, Component }
  });

  return (
    <Routes>
      {routes.map(({ path, authRequired, Component }, index) => (
        <Route
          key={index}
          path={path}
          element={
            authRequired
              ? <ProtectedRoute
                  component={Component}
                  isAuthenticatedUser={AuthService.isAuthenticatedUser()}
                />
              : <Component />
          }
        />
      ))}

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default AppRoutes;
